import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CatalogLayout,
  useFeature,
  useLocalStorageState,
} from '@ole-ui/ole-ui-components';
import { Nav, NavExpandable, NavItem, NavList } from '@patternfly/react-core';
import MyEnrollmentsWidget from 'components/Subscriptions/MyEnrollmentsWidget';
import useMobxStores from 'hooks/useMobxStores';
import { observer } from 'mobx-react';
import { EnablementFeatureFlags } from 'config/featureFlags';
import { LiveEvent } from 'types';
import LaneIds from 'views/MyTraining/Lanes/types';

export enum NavigationItemType {
  Item = 'item',
  Expandable = 'expandable',
}

export enum HomeViews {
  MyActivity = 'my-activity',
  MyTraining = 'my-training',
  MySubscriptions = 'my-subscriptions',
}

type HomeNavigationSubItem = {
  count?: number;
  id: LaneIds;
  name: string;
  onClick: () => void;
};

type HomeNavigationItem = {
  analyticsId?: string;
  className?: string;
  content?: React.ReactNode;
  id: HomeViews;
  isActive?: boolean;
  items?: HomeNavigationSubItem[];
  name: string;
  type: NavigationItemType;
};

type HomeV2NavigationProps = {
  activeItem: HomeViews;
  liveEvents?: LiveEvent[];
  onLaneClick: (laneId: LaneIds) => void;
  setActiveItem: (item: HomeViews) => void;
};

const HomeV2Navigation = ({
  activeItem,
  liveEvents = [],
  onLaneClick,
  setActiveItem,
}: HomeV2NavigationProps) => {
  const { t } = useTranslation();
  const { learningPathStore, progressStore, catalogStore, userStore } =
    useMobxStores();

  const isNewMyTrainingNavigationEnabled = useFeature({
    defaultFlagValue: false,
    flagName: EnablementFeatureFlags.NewMyTrainingNavigation,
    section: 'enablements',
  });

  const savedForLaterCardsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.SavedForLaterCardsEnabled,
    defaultFlagValue: false,
  });

  const [isSubscriptionsExpanded, setIsSubscriptionsExpanded] =
    useLocalStorageState<boolean>('home-my-subscriptions-nav-expanded', true);

  const onSelect = (
    _event: React.FormEvent<HTMLInputElement>,
    result: { itemId: number | string | null },
  ) => {
    if (result.itemId !== undefined && result.itemId !== null) {
      setActiveItem(result.itemId as HomeViews);
    }
  };

  const trialUsersItems = userStore.isRHLSTrialSubscriber
    ? catalogStore.AvailableToMeEntryProgressPair.length
    : 0;

  const skillsAssessmentsCount = catalogStore.skillsAssessmentsAvailable
    ? catalogStore.skillsAssessments.length
    : 0;

  const myTrainingHasItems =
    progressStore.visibleOfferingsWithProgress.length +
      learningPathStore.joinedLearningPaths.length +
      liveEvents.length +
      trialUsersItems +
      skillsAssessmentsCount >
    0;

  const displayMyTraining =
    !isNewMyTrainingNavigationEnabled || myTrainingHasItems;

  const isNavDataLoaded =
    learningPathStore.loaded && // drives "Enrolled skills paths" count
    catalogStore.loaded && // drives trial-subscriber and "Favorites" counts
    !progressStore.isLoadingLastAccessed; // drives "Continue learning" count

  if (!isNavDataLoaded) {
    return null;
  }

  const homeNavigationItems: HomeNavigationItem[] = [
    {
      analyticsId: `${HomeViews.MyActivity}-my-activity-lp`,
      id: HomeViews.MyActivity,
      isActive: activeItem === HomeViews.MyActivity,
      name: t('My activity'),
      type: NavigationItemType.Item,
    },
    ...(displayMyTraining
      ? [
          {
            analyticsId: `${HomeViews.MyTraining}-my-training-lp`,
            className: 'home-sidebar-my-training',
            id: HomeViews.MyTraining,
            isActive: activeItem === HomeViews.MyTraining,
            items: [
              ...(liveEvents.length
                ? [
                    {
                      count: liveEvents.length,
                      id: LaneIds.LiveEvents,
                      name: t('Live Events'),
                      onClick: () => onLaneClick(LaneIds.LiveEvents),
                    },
                  ]
                : []),
              {
                count: progressStore.visibleOfferingsWithProgress.length,
                id: LaneIds.ContinueLearning,
                name: t('Continue learning'),
                onClick: () => onLaneClick(LaneIds.ContinueLearning),
              },
              ...(catalogStore.skillsAssessmentsAvailable
                ? [
                    {
                      count: catalogStore.skillsAssessments.length,
                      id: LaneIds.SkillsAssessments,
                      name: t('Skills Assessments'),
                      onClick: () => onLaneClick(LaneIds.SkillsAssessments),
                    },
                  ]
                : []),
              ...(userStore.isRHLSTrialSubscriber
                ? [
                    {
                      count: catalogStore.AvailableToMeEntryProgressPair.length,
                      id: LaneIds.TrialSubscriptions,
                      name: t('Trial subscription'),
                      onClick: () => onLaneClick(LaneIds.TrialSubscriptions),
                    },
                  ]
                : []),
              {
                count: learningPathStore.joinedLearningPaths.length,
                id: LaneIds.SkillsPaths,
                name: t('Enrolled skills paths'),
                onClick: () => onLaneClick(LaneIds.SkillsPaths),
              },
              {
                count: Math.min(
                  10,
                  catalogStore.newOfferingsCatalogEntries.length,
                ),
                id: LaneIds.NewOfferings,
                name: t('New'),
                onClick: () => onLaneClick(LaneIds.NewOfferings),
              },
              ...(savedForLaterCardsEnabled
                ? [
                    {
                      count: catalogStore.savedForLaterCatalogEntries.length,
                      id: LaneIds.SavedForLater,
                      name: t('Saved for later'),
                      onClick: () => onLaneClick(LaneIds.SavedForLater),
                    },
                  ]
                : []),
            ],
            name: t('My training'),
            type: isNewMyTrainingNavigationEnabled
              ? NavigationItemType.Expandable
              : NavigationItemType.Item,
          },
        ]
      : []),
    {
      className: 'home-sidebar-my-enrollments-widget',
      content: <MyEnrollmentsWidget key="home_metrics" />,
      id: HomeViews.MySubscriptions,
      name: t('My subscriptions'),
      type: NavigationItemType.Expandable,
    },
  ];

  return (
    <CatalogLayout.Filters
      renderMobileMenu={() => null}
      mobileCloseButtonProps={{
        buttonText: t('Close'),
        'aria-label': t('Close and show home page content'),
      }}
    >
      <Nav onSelect={onSelect} aria-label={t('Home navigation')}>
        <NavList>
          {homeNavigationItems.map((navItem) => {
            if (navItem.type === NavigationItemType.Item) {
              return (
                <NavItem
                  aria-label={t('Go to {{name}}', { name: navItem.name })}
                  component="button"
                  data-analytics-id={navItem.analyticsId}
                  id={navItem.id}
                  isActive={navItem.isActive}
                  itemId={navItem.id}
                  key={navItem.id}
                  preventDefault
                >
                  {navItem.name}
                </NavItem>
              );
            }

            return (
              <NavExpandable
                aria-label={t('Go to {{name}}', { name: navItem.name })}
                className={navItem.className}
                data-analytics-id={navItem.analyticsId}
                isActive={activeItem === navItem.id}
                key={navItem.id}
                title={navItem.name}
                {...(navItem.id === 'my-subscriptions' && {
                  isExpanded: isSubscriptionsExpanded,
                  onExpand: (_event: React.MouseEvent, isExpanded: boolean) =>
                    setIsSubscriptionsExpanded(isExpanded),
                })}
              >
                {navItem.content ??
                  navItem.items?.map((item) => (
                    <NavItem
                      className="homev2__nav-expandable-item"
                      component="button"
                      id={item.id}
                      itemId={navItem.id}
                      key={item.id}
                      onClick={item.onClick}
                    >
                      <span>{item.name}</span>

                      {item.count && <span>{item.count}</span>}
                    </NavItem>
                  ))}
              </NavExpandable>
            );
          })}

          {/* {configStore.ebooksPlatformURL && (
                <NavItem
                  component="a"
                  to={configStore.ebooksPlatformURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('Go to Bookshelf platform')}
                  data-analytics-id="bookshelf-my-activity-lp"
                >
                  {t('Access bookshelf')}
                  <ExternalIcon className="homev2__external-icon" />
                </NavItem>
              )}
              <NavItem
                component="a"
                to={SKILLS_ASSESSMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('Go to Skills Assessment platform')}
                data-analytics-id="skills-assessment-my-activity-lp"
              >
                {t('Skills assesment')}
                <ExternalIcon className="homev2__external-icon" />
              </NavItem> */}
        </NavList>
      </Nav>
    </CatalogLayout.Filters>
  );
};

export default observer(HomeV2Navigation);
